import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '../contexts/ApiContext'
import type { Project, User } from '@shared/types'

type SortDir = 'asc' | 'desc'

// Rows per page. Fixed: the rows-per-page selector was removed from the UI.
const PAGE_SIZE = 10


// ── Users tab ────────────────────────────────────────────────────────────────

type UserCol = 'email' | 'name' | 'role' | 'lastLogin' | 'createdAt'

const USER_COLUMNS: { key: UserCol; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'name', label: 'Name' },
  { key: 'role', label: 'Role' },
  { key: 'lastLogin', label: 'Last Login' },
  { key: 'createdAt', label: 'Registered' },
]

interface UserRow {
  email: string
  name: string
  role: string
  lastLogin: string
  createdAt: string
}

function nameFromEmail(email: string): string {
  const local = email.split('@')[0]
  return local
    .split('.')
    .map((part) => part.length === 1 ? part.toUpperCase() + '.' : part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function buildUserRows(users: User[]): UserRow[] {
  return users.map((u) => ({
    email: u.email,
    name: u.name ?? nameFromEmail(u.email),
    role: u.role,
    lastLogin: u.lastSeenAt ? u.lastSeenAt.slice(0, 10) : '—',
    createdAt: u.createdAt ? u.createdAt.slice(0, 10) : '—',
  }))
}

// ── Dashboards tab ───────────────────────────────────────────────────────────

type DashCol =
  | 'user' | 'email' | 'mmsId' | 'purpose'
  | 'dashboardName' | 'dashboardId' | 'client'
  | 'industry' | 'mode' | 'createdDate' | 'lastUpdated'

const DASH_COLUMNS: { key: DashCol; label: string }[] = [
  { key: 'user', label: 'User' },
  { key: 'email', label: 'Email' },
  { key: 'mmsId', label: 'mmsId' },
  { key: 'purpose', label: 'Purpose' },
  { key: 'dashboardName', label: 'Navigator Name' },
  { key: 'mode', label: 'Mode' },
  { key: 'createdDate', label: 'Created Date' },
  { key: 'lastUpdated', label: 'Last Updated' },
]

interface DashRow {
  user: string
  email: string
  mmsId: string
  purpose: string
  dashboardName: string
  dashboardId: string
  client: string
  industry: string
  mode: string
  createdDate: string
  lastUpdated: string
}

function isCompleted(p: Project): boolean {
  return (
    p.isDemo ||
    ((p.data != null || (p.subfunctionCount != null && p.subfunctionCount > 0) || (p.automationBuildCount != null && p.automationBuildCount > 0)) &&
      (!p.data?.subfunctions || p.data.subfunctions.every((sf) => Object.keys(sf.roles ?? {}).length > 0)))
  )
}

function buildDashRows(projects: Project[], nameByEmail: Map<string, string>): DashRow[] {
  return projects.filter(isCompleted).map((p) => {
    const email = p.createdBy ?? ''
    return {
      user: nameByEmail.get(email) ?? email,
      email,
      mmsId: p.mmsId ?? '',
      purpose: p.purpose ?? '',
      dashboardName: p.name,
      dashboardId: p.id,
      client: p.client,
      industry: p.industry,
      mode: p.mode,
      createdDate: p.createdAt ? p.createdAt.slice(0, 10) : '',
      lastUpdated: p.updatedAt ? p.updatedAt.slice(0, 10) : '',
    }
  })
}

// ── Sort helper ──────────────────────────────────────────────────────────────

function sortedRows<T extends object>(rows: T[], col: keyof T, dir: SortDir): T[] {
  return [...rows].sort((a, b) => {
    const cmp = String(a[col]).localeCompare(String(b[col]), undefined, { sensitivity: 'base' })
    return dir === 'asc' ? cmp : -cmp
  })
}

// ── SortIcon ─────────────────────────────────────────────────────────────────

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <span className="ml-1 text-gray-300">↕</span>
  return <span className="ml-1">{dir === 'asc' ? '↑' : '↓'}</span>
}

// ── Pagination ────────────────────────────────────────────────────────────────

function Pagination({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number
  totalPages: number
  total: number
  onPage: (p: number) => void
}) {
  const start = (page - 1) * PAGE_SIZE + 1
  const end = Math.min(page * PAGE_SIZE, total)

  return (
    <div className="flex items-center justify-between px-3 py-2.5 border-t border-gray-200 bg-white rounded-b-lg">
      <div />
      <div className="flex items-center gap-4 text-sm text-gray-500">
        {/* Only rendered for non-empty tables, so total is always > 0 */}
        <span>{`${start}–${end} of ${total}`}</span>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => onPage(1)}
            disabled={page === 1}
            className="px-1.5 py-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-base leading-none"
            title="First page"
          >
            «
          </button>
          <button
            onClick={() => onPage(page - 1)}
            disabled={page === 1}
            className="px-1.5 py-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-base leading-none"
            title="Previous page"
          >
            ‹
          </button>
          <span className="px-2 tabular-nums">{page} / {totalPages}</span>
          <button
            onClick={() => onPage(page + 1)}
            disabled={page >= totalPages}
            className="px-1.5 py-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-base leading-none"
            title="Next page"
          >
            ›
          </button>
          <button
            onClick={() => onPage(totalPages)}
            disabled={page >= totalPages}
            className="px-1.5 py-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-base leading-none"
            title="Last page"
          >
            »
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Component ────────────────────────────────────────────────────────────────

type Tab = 'users' | 'dashboards'

export default function Admin() {
  const api = useApi()
  const navigate = useNavigate()

  const DEFAULT_FILENAME = 'User_Talent_Navigators_Report'

  const [projects, setProjects] = useState<Project[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportFilename, setExportFilename] = useState(DEFAULT_FILENAME)
  const [tab, setTab] = useState<Tab>('users')

  const [userSortCol, setUserSortCol] = useState<UserCol>('name')
  const [userSortDir, setUserSortDir] = useState<SortDir>('asc')
  const [dashSortCol, setDashSortCol] = useState<DashCol>('createdDate')
  const [dashSortDir, setDashSortDir] = useState<SortDir>('desc')

  const [userPage, setUserPage] = useState(1)
  const [dashPage, setDashPage] = useState(1)

  useEffect(() => {
    // A failed access check or data load ends loading with an error instead of an unhandled rejection.
    const fail = () => { setLoadError(true); setLoading(false) }
    api.checkAdminAccess().then(({ isAdmin }) => {
      if (!isAdmin) { navigate('/'); return }
      Promise.all([api.getAdminDashboards(), api.getAdminUsers()])
        .then(([p, u]) => { setProjects(p); setUsers(u); setLoading(false) })
        .catch(fail)
    }).catch(fail)
  }, [api, navigate])

  const nameByEmail = useMemo(
    () => new Map(users.map((u) => [u.email, u.name ?? nameFromEmail(u.email)])),
    [users],
  )

  const userRows = useMemo(() => buildUserRows(users), [users])
  const dashRows = useMemo(() => buildDashRows(projects, nameByEmail), [projects, nameByEmail])

  const sortedUsers = useMemo(
    () => sortedRows(userRows, userSortCol, userSortDir),
    [userRows, userSortCol, userSortDir],
  )
  const sortedDashes = useMemo(
    () => sortedRows(dashRows, dashSortCol, dashSortDir),
    [dashRows, dashSortCol, dashSortDir],
  )

  const userTotalPages = Math.max(1, Math.ceil(sortedUsers.length / PAGE_SIZE))
  const dashTotalPages = Math.max(1, Math.ceil(sortedDashes.length / PAGE_SIZE))

  const pagedUsers = useMemo(() => {
    const start = (userPage - 1) * PAGE_SIZE
    return sortedUsers.slice(start, start + PAGE_SIZE)
  }, [sortedUsers, userPage])

  const pagedDashes = useMemo(() => {
    const start = (dashPage - 1) * PAGE_SIZE
    return sortedDashes.slice(start, start + PAGE_SIZE)
  }, [sortedDashes, dashPage])

  function userRowIndex(i: number) {
    return (userPage - 1) * PAGE_SIZE + i + 1
  }

  function dashRowIndex(i: number) {
    return (dashPage - 1) * PAGE_SIZE + i + 1
  }

  function handleUserSort(col: UserCol) {
    if (col === userSortCol) setUserSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setUserSortCol(col); setUserSortDir('asc') }
    setUserPage(1)
  }

  function handleDashSort(col: DashCol) {
    if (col === dashSortCol) setDashSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setDashSortCol(col); setDashSortDir('asc') }
    setDashPage(1)
  }

  function openExportModal() {
    setExportFilename(DEFAULT_FILENAME)
    setShowExportModal(true)
  }

  async function handleExport() {
    const base = (exportFilename.trim() || DEFAULT_FILENAME).replace(/\.xlsx$/i, '')
    const withExt = `${base}.xlsx`
    setShowExportModal(false)
    setExporting(true)
    try { await api.downloadAdminReport(withExt) }
    finally { setExporting(false) }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🏢</span>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Workspace</h1>
            {!loading && !loadError && (
              <p className="text-sm text-gray-500">
                {users.length} user{users.length !== 1 ? 's' : ''} · {dashRows.length} navigator{dashRows.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={openExportModal}
          disabled={exporting || loading || loadError}
          className={`flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${tab !== 'dashboards' ? 'invisible' : ''}`}
        >
          {exporting ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Exporting…
            </>
          ) : (
            <>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export to Excel
            </>
          )}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-6">
          {(['users', 'dashboards'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`pb-3 text-sm font-medium capitalize border-b-2 transition-colors ${
                tab === t
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t === 'users' ? `Users (${users.length})` : `Talent Navigators (${dashRows.length})`}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48 text-gray-400">Loading…</div>
      ) : loadError ? (
        <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Couldn't load admin data. Refresh the page to try again.
        </div>
      ) : tab === 'users' ? (
        /* ── Users table ─────────────────────────────────────────────────── */
        sortedUsers.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-400">No users found.</div>
        ) : (
          <div className="rounded-lg border border-gray-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">#</th>
                    {USER_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        onClick={() => handleUserSort(col.key)}
                        className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap"
                      >
                        {col.label}
                        <SortIcon active={userSortCol === col.key} dir={userSortDir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {pagedUsers.map((row, i) => (
                    <tr key={row.email} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-gray-400 text-right tabular-nums">{userRowIndex(i)}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.email}</td>
                      <td className="px-3 py-2 text-gray-900 font-medium whitespace-nowrap">{row.name}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          row.role === 'admin'
                            ? 'bg-purple-50 text-purple-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {row.role}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap tabular-nums">{row.lastLogin}</td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap tabular-nums">{row.createdAt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={userPage}
              totalPages={userTotalPages}
              total={sortedUsers.length}
              onPage={setUserPage}
            />
          </div>
        )
      ) : (
        /* ── Dashboards table ────────────────────────────────────────────── */
        sortedDashes.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-400">No talent navigators found.</div>
        ) : (
          <div className="rounded-lg border border-gray-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">#</th>
                    {DASH_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        onClick={() => handleDashSort(col.key)}
                        className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap"
                      >
                        {col.label}
                        <SortIcon active={dashSortCol === col.key} dir={dashSortDir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {pagedDashes.map((row, i) => (
                    <tr key={`${row.dashboardId}-${i}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-gray-400 text-right tabular-nums">{dashRowIndex(i)}</td>
                      <td className="px-3 py-2 text-gray-900 font-medium whitespace-nowrap">{row.user}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.email}</td>
                      <td className="px-3 py-2 text-gray-600">{row.mmsId}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={row.purpose}>{row.purpose}</td>
                      <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{row.dashboardName}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 whitespace-nowrap">
                          {row.mode}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap tabular-nums">{row.createdDate}</td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap tabular-nums">{row.lastUpdated}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={dashPage}
              totalPages={dashTotalPages}
              total={sortedDashes.length}
              onPage={setDashPage}
            />
          </div>
        )
      )}

      {/* Export rename modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Export to Excel</h2>
            <p className="text-sm text-gray-500 mb-4">Enter a filename for the report.</p>
            <input
              type="text"
              value={exportFilename}
              onChange={(e) => setExportFilename(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleExport() }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 mb-5"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
